import { Text, View, Pressable, RefreshControl, Modal, ScrollView, Platform } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { ActivityIndicator } from 'react-native-paper';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import EmptyListPlaceholder from '@/components/General/EmptyListPlaceholder';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import { LegendList } from "@legendapp/list";
import Toast from 'react-native-toast-message';
import { deleteRoleFolder, saveRoleFolder, deleteRole, setPermissionLocked } from '@/data/roles';
import { removeUserFromOrganization, changeConnectedProfile } from '@/data/registrations';
import { changeAdminStatus } from '@/data/people';
import usePlatform from '@/utils/hooks/usePlatform';
import { showAlert } from '@/utils/showAlert';
import { useNavigation, useRouter, useLocalSearchParams } from 'expo-router';
import DropdownMenu, { dropdownIcons } from '@/components/General/DropdownMenu';
import PageTitle from '@/components/General/PageTitle';
import SearchBox from '@/components/General/inputs/SearchBox';
import { TabView } from 'react-native-tab-view';
import ReachTabBar from '@/components/General/ReachTabBar';
import Unicon from '@/components/General/Unicon';
import Avatar from '@/components/People/Avatar';
import ContentLoader from '@/components/General/ContentLoader';
import { Tooltip } from '@/components/General/Tooltip';
import ChangeConnectedProfileModal from '@/components/General/ChangeConnectedProfileModal';
import useOrganizationEntitlements from '@/utils/hooks/useOrganizationEntitlements';
import { isFreeOrg } from '@/utils/organizationEntitlements';
import { roleCounts } from '@/utils/folderPickerModel';

// ============ FOLDER CARD ============
const FolderCard = ({ item, canEdit, canDelete, router, onDelete, onMove }) => {
    const menuOptions = useMemo(() => {
        const opts = [];
        if (canEdit) {
            opts.push({ name: "Edit Folder", icon: dropdownIcons.edit, onSelect: () => router.push({ pathname: "/roles/folders/create", params: { id: item.id } }) });
            opts.push({ name: "Move Folder", icon: { android: "redo", ios: "arrowshape.turn.up.right.fill", web: "arrow-redo" }, onSelect: () => onMove(item) });
        }
        if (canDelete) {
            opts.push({ name: "Delete Folder", destructive: true, icon: dropdownIcons.delete, onSelect: () => showAlert({
                title: "Delete Folder",
                subtitle: "Are you sure you want to delete this folder? All subfolders will be deleted as well, however, roles within this folder will be retained.",
                isDestructive: true,
                onConfirm: () => onDelete(item?.id),
                confirmText: "Delete"
            }) });
        }
        return opts;
    }, [item.id, canEdit, canDelete, router, onDelete, onMove]);

    return (
        <View testID={"FolderCard-" + item.name} style={styles.card}>
            <View style={styles.cardBody}>
                <Pressable
                    style={styles.cardPressable}
                    onPress={() => router.push({ pathname: "/roles", params: { folderID: item.id } })}
                >
                    <View style={styles.folderIconContainer}>
                        <Unicon library="fa5" name="folder" colorVariant="warning" size={20} solid />
                    </View>
                    <View style={styles.cardContent}>
                        <Text numberOfLines={1} style={styles.cardTitle}>{item.name}</Text>
                        <View style={styles.cardMetaRow}>
                            <Unicon name="layers-outline" size={12} colorVariant="textSecondary" />
                            <Text style={styles.metaText}>
                                {(item.Roles?.length || 0)} role{(item.Roles?.length || 0) !== 1 ? 's' : ''}
                            </Text>
                            {item.children?.length > 0 && (
                                <>
                                    <Text style={styles.metaDot}>·</Text>
                                    <Unicon name="folder-outline" size={12} colorVariant="textSecondary" />
                                    <Text style={styles.metaText}>{item.children.length} subfolder{item.children.length !== 1 ? 's' : ''}</Text>
                                </>
                            )}
                        </View>
                    </View>
                </Pressable>
                {menuOptions.length > 0 && <DropdownMenu testID="FolderCardMenu" options={menuOptions} />}
            </View>
        </View>
    );
};

// ============ ROLE CARD ============
const RoleCard = ({ item, canEdit, canDelete, isAdmin, router, onDelete }) => {
    const memberCount = item.People?.[0]?.count || 0;
    const permCount = item.Permissions?.filter(p => !p.locked)?.length || 0;
    const lockedCount = item.Permissions?.filter(p => p.locked)?.length || 0;
    const resourceCount = roleCounts(item).resources;

    const menuOptions = useMemo(() => {
        const opts = [];
        if (canEdit) {
            opts.push({ name: "Edit Role", icon: dropdownIcons.edit, onSelect: () => router.push({ pathname: "/roles/create", params: { id: item.id, folderID: item.folderID } }) });
        }
        if (canDelete) {
            opts.push({ name: "Delete Role", destructive: true, icon: dropdownIcons.delete, onSelect: () => showAlert({
                title: "Delete Role",
                subtitle: "Are you sure you want to delete this role? This action cannot be undone.",
                isDestructive: true,
                onConfirm: () => onDelete(item?.id),
                confirmText: "Delete"
            }) });
        }
        return opts;
    }, [item.id, item.folderID, canEdit, canDelete, router, onDelete]);

    return (
        <View testID={"RoleCard-" + item.name} style={styles.card}>
            <View style={styles.cardBody}>
                <Pressable
                    style={styles.cardPressable}
                    onPress={() => router.push({ pathname: "/roles/" + item.id })}
                >
                    <View style={styles.roleIconContainer}>
                        <Unicon name="shield-checkmark" size={20} colorVariant="primary" />
                    </View>
                    <View style={styles.cardContent}>
                        <View style={styles.titleRow}>
                            <Text numberOfLines={1} style={styles.cardTitle}>{item.name}</Text>
                            {item.isHidden && <Unicon name="eye-off" size={14} colorVariant="textSecondary" style={{ opacity: 0.6 }} />}
                        </View>
                        {!!item.description && <Text numberOfLines={1} style={styles.descriptionText}>{item.description}</Text>}
                        <View style={styles.cardMetaRow}>
                            {item.quickFilterID ? (
                                <View style={styles.dynamicChip}>
                                    <Unicon library="fa5" name="user-check" size={10} colorVariant="info" />
                                    <Text style={styles.dynamicChipText}>Dynamic</Text>
                                </View>
                            ) : (
                                <View style={styles.metaChip}>
                                    <Unicon name="people" size={12} colorVariant="textSecondary" />
                                    <Text style={styles.metaText}>{memberCount}</Text>
                                </View>
                            )}
                            {isAdmin && (
                                <View style={styles.metaChip}>
                                    <Unicon name="key" size={12} colorVariant="textSecondary" />
                                    <Text style={styles.metaText}>{permCount}</Text>
                                </View>
                            )}
                            <View style={styles.metaChip}>
                                <Unicon name="layers-outline" size={12} colorVariant="textSecondary" />
                                <Text style={styles.metaText}>{resourceCount}</Text>
                            </View>
                            {lockedCount > 0 && (
                                <View style={styles.lockedChip}>
                                    <Unicon name="lock-closed" size={10} colorVariant="warning" />
                                </View>
                            )}
                        </View>
                    </View>
                </Pressable>
                {menuOptions.length > 0 && <DropdownMenu testID="RoleCardMenu" options={menuOptions} />}
            </View>
        </View>
    );
};

// ============ PERMISSION CARD ============
const PermissionCard = ({ item, router, isAccountOwner, onToggleLock }) => {
    const roleCount = item.Roles?.[0]?.count || 0;

    return (
        <View testID={"PermissionCard-" + item.name} style={styles.card}>
            <Pressable
                style={[styles.cardBody, styles.cardPressable]}
                onPress={() => router.push({ pathname: "/permissions/" + item.id })}
            >
                <View style={styles.permissionIconContainer}>
                    <Unicon name="key" size={20} colorVariant="secondary" />
                </View>
                <View style={styles.cardContent}>
                    <View style={styles.titleRow}>
                        <Text numberOfLines={1} style={styles.cardTitle}>{item.name}</Text>
                        {!isAccountOwner && item.locked && <Unicon name="lock-closed" size={12} colorVariant="warning" />}
                    </View>
                    {!!item.description && <Text numberOfLines={2} style={styles.descriptionText}>{item.description}</Text>}
                    <View style={styles.cardMetaRow}>
                        <View style={styles.metaChip}>
                            <Unicon name="shield-checkmark" size={12} colorVariant="textSecondary" />
                            <Text style={styles.metaText}>{roleCount} role{roleCount !== 1 ? 's' : ''}</Text>
                        </View>
                    </View>
                </View>
                {isAccountOwner && (
                    <Tooltip
                        title={item.locked ? "Unlock permission" : "Lock permission"}
                        subtitle={item.locked ? "Roles with this permission will be able to be managed by others." : "Roles with this permission cannot be modified while locked."}
                        onPress={(e) => { e?.stopPropagation?.(); onToggleLock(item); }}
                        style={styles.lockToggleButton(item.locked)}
                        testID={`PermissionCard-LockToggle-${item.id}`}
                    >
                        <Unicon name={item.locked ? "lock-closed" : "lock-open-outline"} size={18} colorVariant={item.locked ? "warning" : "textSecondary"} />
                    </Tooltip>
                )}
                <Unicon name="chevron-forward" size={18} colorVariant="textSecondary" />
            </Pressable>
        </View>
    );
};

// ============ MOVE FOLDER MODAL ============
const MoveFolderModal = ({ visible, sourceFolder, allFolders, onClose, onMove, isLoading }) => {
    const [modalSearchText, setModalSearchText] = useState("");
    const [selectedFolderID, setSelectedFolderID] = useState(null);
    const [expandedFolders, setExpandedFolders] = useState(new Set());

    useEffect(() => {
        if (visible) {
            setModalSearchText("");
            setSelectedFolderID(null);
            setExpandedFolders(new Set());
        }
    }, [visible]);

    const excludedIDs = useMemo(() => {
        if (!sourceFolder || !allFolders) return new Set();
        const excluded = new Set([sourceFolder.id]);
        const collect = (folderID) => {
            const folder = allFolders.find(f => f.id === folderID);
            if (folder?.children) {
                folder.children.forEach(child => {
                    excluded.add(child.id);
                    collect(child.id);
                });
            }
        };
        collect(sourceFolder.id);
        return excluded;
    }, [sourceFolder, allFolders]);

    const toggleFolder = (folderID) => {
        setExpandedFolders(prev => {
            const next = new Set(prev);
            if (next.has(folderID)) next.delete(folderID);
            else next.add(folderID);
            return next;
        });
    };

    const buildTree = useCallback((folders, parentID = null) => {
        return folders
            .filter(f => f.parentFolderID === parentID && !excludedIDs.has(f.id))
            .sort((a, b) => a.name.localeCompare(b.name))
            .map(folder => ({
                ...folder,
                children: buildTree(folders, folder.id)
            }));
    }, [excludedIDs]);

    const tree = useMemo(() => buildTree(allFolders || [], null), [allFolders, buildTree]);

    const filteredTree = useMemo(() => {
        if (!modalSearchText.trim()) return tree;
        const term = modalSearchText.toLowerCase();
        const matches = (folder) => {
            if (folder.name?.toLowerCase().includes(term)) return true;
            return folder.children?.some(matches);
        };
        return tree.filter(matches);
    }, [tree, modalSearchText]);

    const renderFolder = (folder, level = 0) => {
        const hasChildren = folder.children && folder.children.length > 0;
        const isSelected = selectedFolderID === folder.id;
        const isExpanded = expandedFolders.has(folder.id) || modalSearchText.trim() !== "";

        return (
            <View key={folder.id}>
                <Pressable
                    onPress={() => setSelectedFolderID(folder.id)}
                    style={[styles.modalFolderRow, { paddingLeft: 16 + level * 20 }, isSelected && styles.modalFolderRowSelected]}
                >
                    {hasChildren ? (
                        <Pressable onPress={() => toggleFolder(folder.id)} style={styles.modalFolderChevron}>
                            <Unicon
                                name={isExpanded ? 'chevron-down' : 'chevron-forward'}
                                size={18}
                                colorVariant="textSecondary"
                            />
                        </Pressable>
                    ) : (
                        <View style={{ width: 26 }} />
                    )}
                    <Unicon library="fa5" name="folder" size={18} colorVariant={isSelected ? 'primary' : 'warning'} style={{ marginRight: 8 }} solid />
                    <Text style={[styles.modalFolderText, isSelected && styles.modalFolderTextSelected]} numberOfLines={1}>
                        {folder.name}
                    </Text>
                </Pressable>
                {hasChildren && isExpanded && folder.children.map(child => renderFolder(child, level + 1))}
            </View>
        );
    };

    return (
        <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
            <Pressable style={styles.modalOverlay} onPress={onClose}>
                <View style={styles.modalInner} onStartShouldSetResponder={() => true}>
                    <Text style={styles.modalTitle}>Move Folder</Text>
                    {sourceFolder && (
                        <Text style={styles.modalSubtitle} numberOfLines={1}>
                            Moving: {sourceFolder.name}
                        </Text>
                    )}
                    <View style={styles.modalSearchWrap}>
                        <SearchBox searchText={modalSearchText} onChangeText={setModalSearchText} placeholder="Search folders..." />
                    </View>
                    <ScrollView style={styles.modalList} keyboardShouldPersistTaps="handled">
                        <Pressable
                            onPress={() => setSelectedFolderID(null)}
                            style={[styles.modalFolderRow, selectedFolderID === null && styles.modalFolderRowSelected]}
                        >
                            <View style={{ width: 26 }} />
                            <Unicon name="home" size={18} colorVariant={selectedFolderID === null ? 'primary' : 'textSecondary'} style={{ marginRight: 8 }} />
                            <Text style={[styles.modalFolderText, selectedFolderID === null && styles.modalFolderTextSelected]}>Home</Text>
                        </Pressable>
                        {filteredTree.map(folder => renderFolder(folder))}
                        {filteredTree.length === 0 && modalSearchText.trim() !== "" && (
                            <Text style={styles.modalEmptyText}>No folders match your search</Text>
                        )}
                    </ScrollView>
                    <View style={styles.modalActions}>
                        <Pressable onPress={onClose} style={[styles.modalButton, styles.modalButtonSecondary]}>
                            <Text style={styles.modalButtonSecondaryText}>Cancel</Text>
                        </Pressable>
                        <Pressable
                            onPress={() => onMove(selectedFolderID)}
                            disabled={isLoading}
                            style={[styles.modalButton, styles.modalButtonPrimary, isLoading && { opacity: 0.6 }]}
                        >
                            {isLoading ? (
                                <ActivityIndicator size="small" color="#fff" />
                            ) : (
                                <Text style={styles.modalButtonPrimaryText}>Move</Text>
                            )}
                        </Pressable>
                    </View>
                </View>
            </Pressable>
        </Modal>
    );
};

// ============ BREADCRUMBS ============
const Breadcrumbs = ({ folderPath, onNavigate }) => (
    <View style={styles.breadcrumbs}>
        <Pressable onPress={() => onNavigate(null)} style={styles.breadcrumbItem}>
            <Unicon name="home" size={14} colorVariant="textSecondary" />
            <Text style={styles.breadcrumbText}>Home</Text>
        </Pressable>
        {folderPath.map((folder, index) => (
            <View key={folder.id} style={styles.breadcrumbItem}>
                <Unicon name="chevron-forward" size={12} colorVariant="textSecondary" />
                <Pressable onPress={() => onNavigate(folder.id)}>
                    <Text style={[styles.breadcrumbText, index === folderPath.length - 1 && styles.breadcrumbActive]}>
                        {folder.name}
                    </Text>
                </Pressable>
            </View>
        ))}
    </View>
);

// ============ USER CARD ============
const UserCard = ({ item, onRemove, onRemoveAdmin, onChangeProfile, isAdmin, selfPersonID, router, isFree }) => {
    const roleCount = item.Person?.Roles?.[0]?.count || 0;
    // The Account Owner cannot be demoted or removed from the org (DB-enforced) — hide those actions
    const canRemoveAdmin = isAdmin && item.isAdmin && !item.isAccountOwner && item.Person?.id !== selfPersonID;

    return (
        <Pressable
            onPress={() => router.push({ pathname: `/people/${item.Person?.id}`, params: isFree ? {} : { tab: 'roles' } })}
            style={styles.userCard}
        >
            <View style={styles.userCardContent}>
                <Avatar profile={item.Person} disableBorder size={44} />
                <View style={styles.userInfo}>
                    <View style={styles.userNameRow}>
                        <Text style={styles.userName} numberOfLines={1}>
                            {item.Person?.firstName} {item.Person?.lastName}
                        </Text>
                        {item.isAccountOwner ? (
                            <View style={styles.ownerBadge} testID={`UserCard-OwnerBadge-${item.Person?.id}`}>
                                <Unicon name="star" size={10} colorVariant="onWarning" />
                                <Text style={styles.ownerBadgeText}>Owner</Text>
                            </View>
                        ) : item.isAdmin && (
                            <View style={styles.adminBadge}>
                                <Unicon name="shield" size={10} colorVariant="onPrimary" />
                                <Text style={styles.adminBadgeText}>Admin</Text>
                            </View>
                        )}
                    </View>
                    <View style={styles.userMetaRow}>
                        {item.Person?.email ? (
                            <Text style={styles.userEmail} numberOfLines={1}>{item.Person.email}</Text>
                        ) : null}
                        {!isFree && roleCount > 0 && (
                            <View style={styles.userRoleChip}>
                                <Unicon name="shield-checkmark" size={11} colorVariant="primary" />
                                <Text style={styles.userRoleChipText}>{roleCount} role{roleCount !== 1 ? 's' : ''}</Text>
                            </View>
                        )}
                    </View>
                </View>
            </View>
            <View style={styles.userActions}>
                {isAdmin && (
                    <Tooltip
                        title="Change Connected Profile"
                        subtitle={`This user is currently connected to ${item.Person?.firstName || 'this profile'}. Use this if the wrong profile was connected when they registered.`}
                        onPress={(e) => { e.stopPropagation(); onChangeProfile(item); }}
                        style={styles.changeProfileButton}
                        testID={`UserCard-ChangeProfile-${item.Person?.id}`}
                    >
                        <Unicon name="swap-horizontal" size={20} colorVariant="textSecondary" />
                    </Tooltip>
                )}
                {canRemoveAdmin && (
                    <Tooltip
                        title="Remove Admin"
                        subtitle="This person will no longer have admin privileges for your organization."
                        onPress={(e) => { e.stopPropagation(); onRemoveAdmin(item); }}
                        style={styles.removeAdminButton}
                        testID={`UserCard-RemoveAdmin-${item.Person?.id}`}
                    >
                        <Unicon name="shield" size={20} colorVariant="error" />
                    </Tooltip>
                )}
                {!item.isAccountOwner && (
                    <Tooltip
                        title="Remove"
                        subtitle="Removes this person from your organization. They will no longer be allowed to log in."
                        onPress={(e) => { e.stopPropagation(); onRemove(item); }}
                        style={styles.removeButton}
                        testID={`UserCard-Remove-${item.Person?.id}`}
                    >
                        <Unicon name="close-circle-outline" size={20} colorVariant="error" />
                    </Tooltip>
                )}
            </View>
        </Pressable>
    );
};

// ============ TAB COMPONENTS ============
const RolesTabContent = ({ searchText, onChangeText, isAdmin, folderPath, onBreadcrumbNavigate, rolesData, isFetching, renderRoleItem, refreshList }) => (
    <View style={styles.tabContent}>
        <View style={styles.searchContainer}>
            <SearchBox testID="Roles" searchText={searchText} onChangeText={onChangeText} placeholder="Search roles..." />
        </View>

        {/* Breadcrumbs - visible when not searching and user has role management access */}
        {searchText === "" && isAdmin && (
            <View style={styles.breadcrumbContainer}>
                <Breadcrumbs folderPath={folderPath} onNavigate={onBreadcrumbNavigate} />
            </View>
        )}

        <View style={styles.listContainer}>
            <LegendList
                recycleItems={Platform.OS !== 'web'}
                keyExtractor={(item) => item.id}
                showsVerticalScrollIndicator={false}
                keyboardShouldPersistTaps="handled"
                estimatedItemSize={80}
                data={isFetching ? [] : rolesData}
                renderItem={renderRoleItem}
                contentContainerStyle={styles.listContent}
                refreshControl={<RefreshControl refreshing={false} onRefresh={refreshList} />}
                ListFooterComponent={
                    isFetching
                        ? <View style={styles.loadingContainer}><ActivityIndicator size="large" /></View>
                        : rolesData?.length === 0
                            ? <EmptyListPlaceholder icon="lock-open-outline" spacing={10} message={searchText ? "No roles match your search" : "No roles in this folder"} />
                            : <View style={styles.listFooterSpacer} />
                }
            />
        </View>
    </View>
);

const PermissionsTabContent = ({ searchText, onChangeText, permissionsData, isFetchingPermissions, renderPermissionItem, refreshList }) => (
    <View style={styles.tabContent}>
        <View style={styles.searchContainer}>
            <SearchBox testID="Permissions" searchText={searchText} onChangeText={onChangeText} placeholder="Search permissions..." />
        </View>
        <View style={styles.listContainer}>
            <LegendList
                recycleItems={Platform.OS !== 'web'}
                keyExtractor={(item) => item.id}
                showsVerticalScrollIndicator={false}
                keyboardShouldPersistTaps="handled"
                estimatedItemSize={80}
                data={isFetchingPermissions ? [] : permissionsData}
                renderItem={renderPermissionItem}
                contentContainerStyle={styles.listContent}
                refreshControl={<RefreshControl refreshing={false} onRefresh={refreshList} />}
                ListFooterComponent={
                    isFetchingPermissions
                        ? <View style={styles.loadingContainer}><ActivityIndicator size="large" /></View>
                        : permissionsData?.length === 0
                            ? <EmptyListPlaceholder icon="lock-open-outline" spacing={10} message={searchText ? "No permissions match your search" : "No permissions to display"} />
                            : <View style={styles.listFooterSpacer} />
                }
            />
        </View>
    </View>
);

const UsersTabContent = ({ searchText, onChangeText, showAdminsOnly, onToggleAdmins, filteredUsers, loadingRegisteredUsers, registeredUsers, adminCount, renderUserItem, refreshList }) => (
    <View style={styles.tabContent}>
        <View style={styles.searchContainer}>
            <View style={styles.usersToolbarRow}>
                <View style={styles.usersSearchWrapper}>
                    <SearchBox testID="Users" searchText={searchText} onChangeText={onChangeText} placeholder="Search users..." textChangeDelay={200} />
                </View>
                <Pressable
                    onPress={onToggleAdmins}
                    style={styles.filterButton(showAdminsOnly)}
                >
                    <Unicon name="shield" size={16} colorVariant={showAdminsOnly ? "onPrimary" : "textSecondary"} />
                    <Text style={styles.filterButtonText(showAdminsOnly)}>Admins</Text>
                </Pressable>
            </View>
            {!loadingRegisteredUsers && (
                <Text style={styles.usersSubtitle}>
                    {registeredUsers?.length || 0} user{registeredUsers?.length !== 1 ? 's' : ''} · {adminCount} admin{adminCount !== 1 ? 's' : ''}
                </Text>
            )}
        </View>
        <View style={styles.listContainer}>
            <LegendList
                key={`users-${showAdminsOnly}`}
                recycleItems={false}
                keyExtractor={(item) => item.userID}
                estimatedItemSize={88}
                showsVerticalScrollIndicator={false}
                keyboardShouldPersistTaps="handled"
                data={loadingRegisteredUsers ? [] : filteredUsers}
                renderItem={renderUserItem}
                contentContainerStyle={styles.listContent}
                refreshControl={<RefreshControl refreshing={false} onRefresh={refreshList} />}
                ListEmptyComponent={
                    !loadingRegisteredUsers && (
                        searchText.trim() || showAdminsOnly
                            ? <EmptyListPlaceholder icon="search-outline" message="No users match your search" />
                            : <EmptyListPlaceholder icon="people-outline" message="No registered users" />
                    )
                }
                ListFooterComponent={
                    loadingRegisteredUsers
                        ? <View style={styles.loaderContainer}>
                            <ContentLoader />
                            <ContentLoader staggerPosition={1} />
                            <ContentLoader staggerPosition={2} />
                            <ContentLoader staggerPosition={3} />
                            <ContentLoader staggerPosition={4} />
                          </View>
                        : <View style={styles.listFooterSpacer} />
                }
            />
        </View>
    </View>
);

// ============ MAIN SCREEN ============
export default function RoleListScreen() {
    const { folderID, tab } = useLocalSearchParams();
    const [selectedTabIndex, setSelectedTabIndex] = useState(0);
    const [rolesData, setRolesData] = useState([]);
    const [permissionsData, setPermissionsData] = useState([]);
    const [searchText, setSearchText] = useState("");
    const [moveModalVisible, setMoveModalVisible] = useState(false);
    const [moveFolderSource, setMoveFolderSource] = useState(null);
    const [showAdminsOnly, setShowAdminsOnly] = useState(false);
    const [changeProfileUser, setChangeProfileUser] = useState(null);

    const { isNative } = usePlatform();
    const navigation = useNavigation();
    const router = useRouter();

    const queryClient = useQueryClient();
    const { data: loggedInUser } = Query.people.loggedInUser();
    // Free admins manage Users here, without querying paid Roles/Permissions.
    const { entitlements } = useOrganizationEntitlements();
    const isFree = isFreeOrg(entitlements);
    const { data: roles, isFetching } = Query.roleFolders.all({ user: loggedInUser, options: { enabled: !!loggedInUser && !isFree } });
    const { data: permissions, isFetching: isFetchingPermissions } = Query.permissions.all({ options: { enabled: !!loggedInUser && !isFree } });
    const { data: registeredUsers, isFetching: loadingRegisteredUsers } = Query.registeredUsers.all({ options: { enabled: !!loggedInUser } });
    const tabRoutes = useMemo(() => isFree
        ? [{ key: 'users', title: 'Users' }]
        : loggedInUser?.isAdmin
            ? [{ key: 'roles', title: 'Roles' }, { key: 'permissions', title: 'Permissions' }, { key: 'users', title: 'Users' }]
            : [{ key: 'roles', title: 'Roles' }], [isFree, loggedInUser?.isAdmin]);
    const activeTabIndex = Math.min(selectedTabIndex, tabRoutes.length - 1);
    const activeTab = tabRoutes[activeTabIndex];
    useEffect(() => {
        const targetIndex = tabRoutes.findIndex(route => route.key === tab);
        if (targetIndex >= 0) setSelectedTabIndex(targetIndex);
    }, [tab, tabRoutes]);

    const renderTabBar = useMemo(() => props => <ReachTabBar {...props} />, []);

    const deleteFolderMutation = useMutation({
        mutationFn: deleteRoleFolder,
        onSuccess: () => {
            Toast.show({ type: "success", text1: "Role Folder deleted successfully" });
            queryClient.invalidateQueries({ queryKey: QueryKeys.roleFolders.all() });
        }
    });

    const deleteRoleMutation = useMutation({
        mutationFn: deleteRole,
        onSuccess: () => {
            Toast.show({ type: "success", text1: "Role deleted successfully" });
            queryClient.invalidateQueries({ queryKey: QueryKeys.roleFolders.all() });
        }
    });

    const saveFolderMutation = useMutation({
        mutationFn: saveRoleFolder,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QueryKeys.roleFolders.all() });
            setMoveModalVisible(false);
            setMoveFolderSource(null);
        }
    });

    // Build folder path from the root to the current folderID
    const folderPath = useMemo(() => {
        if (!folderID || !roles?.allFolders) return [];
        const path = [];
        let current = roles.allFolders.find(f => f.id === folderID);
        while (current) {
            path.unshift(current);
            current = current.parentFolderID ? roles.allFolders.find(f => f.id === current.parentFolderID) : null;
        }
        return path;
    }, [folderID, roles?.allFolders]);

    // Current folder object
    const currentFolder = useMemo(() => {
        if (!folderID || !roles?.allFolders) return null;
        return roles.allFolders.find(f => f.id === folderID) || null;
    }, [folderID, roles?.allFolders]);

    // Header title: show breadcrumb path
    const headerSubtitle = useMemo(() => {
        if (selectedTabIndex !== 0 || folderPath.length === 0) return "";
        return folderPath.map(f => f.name).join(" › ");
    }, [selectedTabIndex, folderPath]);

    const adminCount = useMemo(() => registeredUsers?.filter(u => u.isAdmin)?.length || 0, [registeredUsers]);

    useEffect(() => {
        navigation.setOptions({
            headerTitle: () => <PageTitle title={activeTab.title} subtitle={isFree ? '' : headerSubtitle} />,
            headerRight: () => (
                !isFree && loggedInUser?.can?.('Create Roles') && activeTab.key === 'roles' ? (
                    <DropdownMenu testID="PageMenu" options={[
                        {
                            name: "Create Role",
                            icon: { android: "group_add", ios: "person.crop.circle.fill.badge.plus", web: "person-add-outline" },
                            onSelect: () => router.push({ pathname: "/roles/create", params: { folderID: folderID || null } })
                        },
                        {
                            name: "Create a Folder",
                            icon: { android: "create_new_folder", ios: "folder.badge.plus", web: "folder-outline" },
                            onSelect: () => router.push({ pathname: "/roles/folders/create", params: { parentFolderID: folderID || null } })
                        }
                    ]} />
                ) : null
            ),
        });
    }, [navigation, folderID, activeTab, isFree, loggedInUser, headerSubtitle]);

    useEffect(() => {
        const list = permissions?.filter((t) => {
            if (!loggedInUser?.isAdmin) return false;
            if (searchText === "" || t.name?.toLowerCase().includes(searchText.toLowerCase()) || t.description?.toLowerCase().includes(searchText.toLowerCase())) return true;
            return false;
        });
        setPermissionsData(list);
    }, [permissions, searchText]);

    useEffect(() => {
        const managingRoleIDs = loggedInUser?.ManagingRoles?.map(r => r.id);
        const hasRolePerms = loggedInUser?.canAny?.(['Create Roles', 'Edit Roles', 'Delete Roles']);

        if (searchText === "" && (loggedInUser?.isAdmin || hasRolePerms)) {
            if (!currentFolder) {
                setRolesData([...(roles?.children || []), ...(roles?.Roles || [])]);
            } else {
                setRolesData([...(currentFolder.children || []), ...(currentFolder.Roles || [])]);
            }
        } else {
            const term = searchText.toLowerCase();
            const roleList = roles?.allRoles?.filter((t) => {
                if (!loggedInUser?.isAdmin && !hasRolePerms && !managingRoleIDs?.some(id => id === t.id)) return false;
                if (!loggedInUser?.isAdmin && hasRolePerms && t.isAccessControl && !managingRoleIDs?.some(id => id === t.id)) return false;
                if (searchText === "" || t.name?.toLowerCase().includes(term) || t.description?.toLowerCase().includes(term)) return true;
                return false;
            }) || [];
            let folderList = [];
            if (searchText !== "") {
                folderList = roles?.allFolders?.filter((f) => {
                    return f.name?.toLowerCase().includes(term);
                }) || [];
            }
            setRolesData([...folderList, ...roleList]);
        }
    }, [roles, searchText, currentFolder, loggedInUser]);

    const removeUserFromOrgMutation = useMutation({
        mutationFn: removeUserFromOrganization,
        onSuccess: (data) => {
            const name = [data?.Person?.firstName, data?.Person?.lastName].filter(Boolean).join(' ') || 'User';
            Toast.show({ type: 'success', text2: `${name} has been removed from your organization` });
            queryClient.invalidateQueries({ queryKey: QueryKeys.registeredUsers.all() });
            queryClient.invalidateQueries({ queryKey: QueryKeys.people.all() });
        }
    });

    const handleRemoveUser = useCallback((item) => {
        showAlert({
            title: `Remove ${item.Person?.firstName} from your organization?`,
            subtitle: `They will no longer be able to login to Reach under your organization. All of their data will still be available and they may be invited back at any time.`,
            confirmText: "Remove From Organization",
            isDestructive: true,
            onConfirm: () => removeUserFromOrgMutation.mutate({ personID: item.Person?.id, userID: item.userID })
        });
    }, [removeUserFromOrgMutation]);

    const removeAdminMutation = useMutation({
        mutationFn: changeAdminStatus,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QueryKeys.registeredUsers.all() });
        }
    });

    const changeConnectedProfileMutation = useMutation({
        mutationFn: changeConnectedProfile,
        onSuccess: () => {
            Toast.show({ type: 'success', text2: 'Connected profile updated' });
            queryClient.invalidateQueries({ queryKey: QueryKeys.registeredUsers.all() });
            queryClient.invalidateQueries({ queryKey: QueryKeys.people.all() });
            setChangeProfileUser(null);
        }
    });

    const handleRemoveAdmin = useCallback((item) => {
        showAlert({
            title: `Remove ${item.Person?.firstName} as admin?`,
            subtitle: `They will no longer have admin privileges for your organization.`,
            confirmText: "Remove Admin",
            isDestructive: true,
            onConfirm: () => removeAdminMutation.mutate({ personID: item.Person?.id, isAdmin: false })
        });
    }, [removeAdminMutation]);

    // Account Owner only: lock/unlock a permission for this org (OrganizationLockedPermissions row)
    const togglePermissionLockMutation = useMutation({
        mutationFn: setPermissionLocked,
        onSuccess: (success, variables) => {
            if (!success) return;
            queryClient.invalidateQueries({ queryKey: QueryKeys.permissions.all() });
            queryClient.invalidateQueries({ queryKey: QueryKeys.permissions.byId(variables?.permissionID) });
            queryClient.invalidateQueries({ queryKey: QueryKeys.roleFolders.all() });
        }
    });

    // Locking is the safe direction (no confirmation). Unlocking needs a confirm —
    // roles with the permission become manageable by others.
    const handleTogglePermissionLock = useCallback((item) => {
        if (item.locked) {
            showAlert({
                title: `Unlock ${item.name}?`,
                subtitle: "Roles with this permission will now be able to be managed by others.",
                confirmText: "Unlock",
                isDestructive: false,
                onConfirm: () => togglePermissionLockMutation.mutate({ permissionID: item.id, locked: false })
            });
        } else {
            togglePermissionLockMutation.mutate({ permissionID: item.id, locked: true });
        }
    }, [togglePermissionLockMutation]);

    const filteredUsers = useMemo(() => {
        if (!registeredUsers) return [];
        let list = registeredUsers;
        if (showAdminsOnly) list = list.filter(u => u.isAdmin);
        if (searchText.trim()) {
            const term = searchText.toLowerCase();
            list = list.filter(u => {
                const name = `${u.Person?.firstName} ${u.Person?.lastName}`.toLowerCase();
                const email = (u.Person?.email || '').toLowerCase();
                return name.includes(term) || email.includes(term);
            });
        }
        return list;
    }, [registeredUsers, searchText, showAdminsOnly]);

    const refreshList = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: QueryKeys.roleFolders.all() });
        queryClient.invalidateQueries({ queryKey: QueryKeys.permissions.all() });
        queryClient.invalidateQueries({ queryKey: QueryKeys.registeredUsers.all() });
    }, [queryClient]);

    const handleBreadcrumbNavigate = useCallback((targetFolderID) => {
        if (targetFolderID) {
            router.push({ pathname: "/roles", params: { folderID: targetFolderID } });
        } else {
            router.push({ pathname: "/roles" });
        }
    }, [router]);

    const renderRoleItem = useCallback(({ item }) => {
        if (item.isFolder) {
            return (
                <FolderCard
                    item={item}
                    canEdit={loggedInUser?.can?.('Edit Roles')}
                    canDelete={loggedInUser?.can?.('Delete Roles')}
                    router={router}
                    onDelete={(id) => deleteFolderMutation.mutate(id)}
                    onMove={(folder) => {
                        setMoveFolderSource(folder);
                        setMoveModalVisible(true);
                    }}
                />
            );
        }
        const isRoleOwner = loggedInUser?.ManagingRoles?.some(r => r.id === item.id);
        return (
            <RoleCard
                item={item}
                canEdit={loggedInUser?.isAdmin || (!item.isAccessControl && loggedInUser?.can?.('Edit Roles')) || isRoleOwner}
                canDelete={loggedInUser?.isAdmin || (!item.isAccessControl && loggedInUser?.can?.('Delete Roles'))}
                isAdmin={loggedInUser?.isAdmin}
                router={router}
                onDelete={(id) => deleteRoleMutation.mutate({ roleID: id })}
            />
        );
    }, [loggedInUser, router, deleteFolderMutation, deleteRoleMutation]);

    const renderPermissionItem = useCallback(({ item }) => {
        return (
            <PermissionCard
                item={item}
                router={router}
                isAccountOwner={loggedInUser?.isAccountOwner}
                onToggleLock={handleTogglePermissionLock}
            />
        );
    }, [router, loggedInUser?.isAccountOwner, handleTogglePermissionLock]);

    const renderUserItem = useCallback(({ item }) => {
        return (
            <UserCard
                item={item}
                onRemove={handleRemoveUser}
                onRemoveAdmin={handleRemoveAdmin}
                onChangeProfile={setChangeProfileUser}
                isAdmin={loggedInUser?.isAdmin}
                selfPersonID={loggedInUser?.id}
                router={router}
                isFree={isFree}
            />
        );
    }, [handleRemoveUser, handleRemoveAdmin, loggedInUser, router, isFree]);

    // People already connected to a registered user in this org (excluding the one being changed)
    const connectedPersonIds = useMemo(
        () => (registeredUsers || [])
            .filter(u => u.Person?.id && u.userID !== changeProfileUser?.userID)
            .map(u => u.Person.id),
        [registeredUsers, changeProfileUser]
    );

    const handleChangeConnectedProfile = useCallback((person) => {
        if (!changeProfileUser?.userID || !person?.id) return;
        changeConnectedProfileMutation.mutate({ userID: changeProfileUser.userID, personID: person.id });
    }, [changeProfileUser, changeConnectedProfileMutation]);

    const renderScene = useCallback(({ route }) => {
        switch (route.key) {
            case 'roles':
                return (
                    <RolesTabContent
                        searchText={searchText}
                        onChangeText={setSearchText}
                        isAdmin={loggedInUser?.isAdmin || loggedInUser?.canAny?.(['Create Roles', 'Edit Roles', 'Delete Roles'])}
                        folderPath={folderPath}
                        onBreadcrumbNavigate={handleBreadcrumbNavigate}
                        rolesData={rolesData}
                        isFetching={isFetching}
                        renderRoleItem={renderRoleItem}
                        refreshList={refreshList}
                    />
                );
            case 'permissions':
                return (
                    <PermissionsTabContent
                        searchText={searchText}
                        onChangeText={setSearchText}
                        permissionsData={permissionsData}
                        isFetchingPermissions={isFetchingPermissions}
                        renderPermissionItem={renderPermissionItem}
                        refreshList={refreshList}
                    />
                );
            case 'users':
                return (
                    <UsersTabContent
                        searchText={searchText}
                        onChangeText={setSearchText}
                        showAdminsOnly={showAdminsOnly}
                        onToggleAdmins={() => setShowAdminsOnly(v => !v)}
                        filteredUsers={filteredUsers}
                        loadingRegisteredUsers={loadingRegisteredUsers}
                        registeredUsers={registeredUsers}
                        adminCount={adminCount}
                        renderUserItem={renderUserItem}
                        refreshList={refreshList}
                    />
                );
            default:
                return null;
        }
    }, [searchText, loggedInUser?.isAdmin, folderPath, handleBreadcrumbNavigate, rolesData, isFetching, renderRoleItem, refreshList, permissionsData, isFetchingPermissions, renderPermissionItem, showAdminsOnly, filteredUsers, loadingRegisteredUsers, registeredUsers, adminCount, renderUserItem]);

    return (
        <View style={styles.container}>
            <View style={styles.contentWrapper}>
                <MoveFolderModal
                    visible={moveModalVisible}
                    sourceFolder={moveFolderSource}
                    allFolders={roles?.allFolders || []}
                    onClose={() => {
                        setMoveModalVisible(false);
                        setMoveFolderSource(null);
                    }}
                    onMove={(parentFolderID) => {
                        if (moveFolderSource) {
                            saveFolderMutation.mutate({ id: moveFolderSource.id, parentFolderID });
                        }
                    }}
                    isLoading={saveFolderMutation.isPending}
                />
                <ChangeConnectedProfileModal
                    isOpen={!!changeProfileUser}
                    onClose={() => setChangeProfileUser(null)}
                    onConfirm={handleChangeConnectedProfile}
                    userName={changeProfileUser?.Person ? `${changeProfileUser.Person.firstName} ${changeProfileUser.Person.lastName}`.trim() : ''}
                    currentPerson={changeProfileUser?.Person}
                    excludePersonIds={connectedPersonIds}
                    loading={changeConnectedProfileMutation.isPending}
                />
                <View style={styles.tabWrapper}>
                    <TabView
                        swipeEnabled={isNative}
                        renderTabBar={isFree || loggedInUser?.isAdmin ? renderTabBar : () => null}
                        navigationState={{
                            index: activeTabIndex,
                            routes: tabRoutes,
                        }}
                        renderScene={renderScene}
                        onIndexChange={setSelectedTabIndex}
                    />
                </View>
            </View>
        </View>
    );
}

const styles = StyleSheet.create((theme) => ({
    container: { flex: 1 },
    contentWrapper: { flex: 1, maxWidth: 700, width: "100%", alignSelf: "center" },
    tabWrapper: { flex: 1 },
    tabContent: { flex: 1 },
    searchContainer: { paddingHorizontal: 16, paddingTop: 8, paddingBottom: 4 },
    listContainer: { flex: 1, paddingHorizontal: 16 },
    listContent: { paddingBottom: 30 },
    loadingContainer: { padding: 40, alignItems: "center", justifyContent: "center" },
    listFooterSpacer: { paddingBottom: 30 },
    loaderContainer: { gap: 8, paddingTop: 8 },

    // Users Tab
    usersToolbarRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: 10,
    },
    usersSearchWrapper: { flex: 1 },
    usersSubtitle: {
        fontSize: 13,
        color: theme.colors.textSecondary,
        marginTop: 6,
        marginBottom: 2,
    },
    filterButton: (active) => ({
        flexDirection: "row",
        alignItems: "center",
        gap: 6,
        paddingHorizontal: 14,
        height: 42,
        borderRadius: 10,
        borderWidth: 1,
        borderColor: active ? theme.colors.primary : theme.colors.border,
        backgroundColor: active ? theme.colors.primary : theme.colors.surface,
        _web: {
            cursor: "pointer",
            transition: "all 0.15s ease",
            userSelect: "none",
            _hover: active ? {} : { borderColor: theme.colors.outline },
        },
    }),
    filterButtonText: (active) => ({
        fontSize: 13,
        fontFamily: theme.fonts.bold,
        color: active ? theme.colors.onPrimary : theme.colors.textSecondary,
    }),
    userCard: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        backgroundColor: theme.colors.surface,
        borderRadius: 10,
        paddingHorizontal: 14,
        paddingVertical: 12,
        marginTop: 8,
        _web: {
            cursor: "pointer",
            transition: "all 0.15s ease",
        },
    },
    userCardContent: {
        flexDirection: "row",
        alignItems: "center",
        flex: 1,
        gap: 12,
        minWidth: 0,
    },
    userInfo: { flex: 1, minWidth: 0 },
    userNameRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: 8,
        flexWrap: "wrap",
    },
    userName: {
        fontSize: 15,
        fontFamily: theme.fonts.heading,
        color: theme.colors.text,
    },
    adminBadge: {
        flexDirection: "row",
        alignItems: "center",
        gap: 4,
        backgroundColor: theme.colors.primary,
        paddingHorizontal: 8,
        paddingVertical: 2,
        borderRadius: 10,
    },
    adminBadgeText: {
        fontSize: 10,
        fontFamily: theme.fonts.bold,
        color: theme.colors.onPrimary,
        textTransform: "uppercase",
    },
    ownerBadge: {
        flexDirection: "row",
        alignItems: "center",
        gap: 4,
        backgroundColor: theme.colors.warning,
        paddingHorizontal: 8,
        paddingVertical: 2,
        borderRadius: 10,
    },
    ownerBadgeText: {
        fontSize: 10,
        fontFamily: theme.fonts.bold,
        color: theme.colors.onWarning,
        textTransform: "uppercase",
    },
    userMetaRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: 8,
        marginTop: 2,
        flexWrap: "wrap",
    },
    userEmail: {
        fontSize: 13,
        color: theme.colors.textSecondary,
        flexShrink: 1,
    },
    userRoleChip: {
        flexDirection: "row",
        alignItems: "center",
        gap: 4,
    },
    userRoleChipText: {
        fontSize: 12,
        color: theme.colors.primary,
    },
    removeButton: {
        padding: 8,
        borderRadius: 8,
        marginLeft: 8,
        _web: {
            cursor: "pointer",
            transition: "all 0.15s ease",
            opacity: 0.6,
            _hover: {
                opacity: 1,
                backgroundColor: theme.colors.errorContainer,
            },
        },
    },
    userActions: {
        flexDirection: "row",
        alignItems: "center",
    },
    removeAdminButton: {
        padding: 8,
        borderRadius: 8,
        marginLeft: 8,
        _web: {
            cursor: "pointer",
            transition: "all 0.15s ease",
            opacity: 0.6,
            _hover: {
                opacity: 1,
                backgroundColor: theme.colors.errorContainer,
            },
        },
    },
    changeProfileButton: {
        padding: 8,
        borderRadius: 8,
        marginLeft: 8,
        _web: {
            cursor: "pointer",
            transition: "all 0.15s ease",
            opacity: 0.6,
            _hover: {
                opacity: 1,
                backgroundColor: theme.colors.primaryContainer,
            },
        },
    },

    // Breadcrumbs
    breadcrumbContainer: { paddingHorizontal: 16, paddingVertical: 8 },
    breadcrumbs: { flexDirection: "row", alignItems: "center", flexWrap: "wrap", gap: 4 },
    breadcrumbItem: { flexDirection: "row", alignItems: "center", gap: 4 },
    breadcrumbText: { fontSize: 13, color: theme.colors.textSecondary },
    breadcrumbActive: { color: theme.colors.text, fontFamily: theme.fonts.heading },

    // Card (shared)
    card: {
        backgroundColor: theme.colors.surface,
        borderRadius: 12,
        marginTop: 8,
        overflow: "hidden",
        _web: { transition: "all 0.15s ease-in-out", _hover: { backgroundColor: theme.colors.hover } },
    },
    cardBody: { flexDirection: "row", alignItems: "center", padding: 14, gap: 12 },
    cardPressable: { flex: 1, flexDirection: "row", alignItems: "center", gap: 12 },
    cardContent: { flex: 1 },
    cardTitle: { fontSize: 15, fontFamily: theme.fonts.heading, color: theme.colors.text },
    titleRow: { flexDirection: "row", alignItems: "center", gap: 6 },
    descriptionText: { fontSize: 12, color: theme.colors.textSecondary, marginTop: 2 },
    cardMetaRow: { flexDirection: "row", alignItems: "center", flexWrap: "wrap", gap: 8, marginTop: 6 },
    metaChip: { flexDirection: "row", alignItems: "center", gap: 3 },
    metaText: { fontSize: 12, color: theme.colors.textSecondary },
    metaDot: { color: theme.colors.textSecondary, fontSize: 12 },

    // Folder Card Icon
    folderIconContainer: {
        width: 40, height: 40, borderRadius: 10,
        backgroundColor: theme.colors.warningContainer,
        alignItems: "center", justifyContent: "center",
    },

    // Role Card Icon
    roleIconContainer: {
        width: 40, height: 40, borderRadius: 10,
        backgroundColor: theme.colors.primaryContainer,
        alignItems: "center", justifyContent: "center",
    },

    // Permission Card Icon
    permissionIconContainer: {
        width: 40, height: 40, borderRadius: 10,
        backgroundColor: theme.colors.secondaryContainer,
        alignItems: "center", justifyContent: "center",
    },

    // Permission Lock Toggle (Account Owner only)
    lockToggleButton: (locked) => ({
        padding: 8,
        borderRadius: 8,
        _web: {
            cursor: "pointer",
            transition: "all 0.15s ease",
            opacity: locked ? 1 : 0.6,
            _hover: {
                opacity: 1,
                backgroundColor: theme.colors.warningContainer,
            },
        },
    }),

    // Chips
    dynamicChip: {
        flexDirection: "row", alignItems: "center", gap: 4,
        backgroundColor: theme.colors.onInfo,
        paddingHorizontal: 8, paddingVertical: 2, borderRadius: 6,
    },
    dynamicChipText: { fontSize: 11, fontFamily: theme.fonts.bold, color: theme.colors.info },
    lockedChip: { opacity: 0.7 },

    // Move Folder Modal
    modalOverlay: {
        flex: 1,
        backgroundColor: theme.colors.background === "#000" ? "rgba(0,0,0,0.7)" : "rgba(0,0,0,0.4)",
        justifyContent: "center",
        alignItems: "center",
        padding: 20,
    },
    modalInner: {
        backgroundColor: theme.colors.background,
        borderRadius: 12,
        width: "100%",
        maxWidth: 420,
        maxHeight: "80%",
        padding: 20,
        overflow: "hidden",
        boxShadow: "0px 0px 8px 0px rgba(0,0,0,0.3)",
    },
    modalTitle: {
        fontSize: 18,
        fontFamily: theme.fonts.heading,
        color: theme.colors.text,
        marginBottom: 4,
    },
    modalSubtitle: {
        fontSize: 13,
        color: theme.colors.textSecondary,
        marginBottom: 12,
    },
    modalSearchWrap: {
        marginBottom: 12,
    },
    modalList: {
        flex: 1,
        marginBottom: 12,
    },
    modalFolderRow: {
        flexDirection: "row",
        alignItems: "center",
        paddingVertical: 10,
        paddingRight: 12,
        borderRadius: 8,
    },
    modalFolderRowSelected: {
        backgroundColor: theme.colors.primaryContainer,
    },
    modalFolderChevron: {
        marginRight: 4,
        padding: 2,
    },
    modalFolderText: {
        fontSize: 14,
        color: theme.colors.text,
        flex: 1,
    },
    modalFolderTextSelected: {
        color: theme.colors.primary,
        fontFamily: theme.fonts.bold,
    },
    modalEmptyText: {
        textAlign: "center",
        color: theme.colors.textSecondary,
        paddingVertical: 20,
        fontSize: 13,
    },
    modalActions: {
        flexDirection: "row",
        justifyContent: "flex-end",
        gap: 10,
        paddingTop: 4,
    },
    modalButton: {
        paddingHorizontal: 16,
        paddingVertical: 10,
        borderRadius: 8,
        alignItems: "center",
        justifyContent: "center",
        minWidth: 80,
    },
    modalButtonSecondary: {
        backgroundColor: theme.colors.surface,
    },
    modalButtonSecondaryText: {
        fontSize: 14,
        color: theme.colors.textSecondary,
        fontFamily: theme.fonts.bold,
    },
    modalButtonPrimary: {
        backgroundColor: theme.colors.primary,
    },
    modalButtonPrimaryText: {
        fontSize: 14,
        color: theme.colors.onPrimary,
        fontFamily: theme.fonts.bold,
    },
}))